import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Upload, PencilSimple, Trash } from "@phosphor-icons/react";
import ItemFormDialog from "@/components/inventory/ItemFormDialog";
import ItemImportDialog from "@/components/inventory/ItemImportDialog";
import Pagination from "@/components/Pagination";

const CATEGORIES = [
  { v: "raw_material", l: "Raw Material" },
  { v: "packaging", l: "Packaging" },
  { v: "consumables", l: "Consumables" },
  { v: "finished_goods", l: "Finished Goods" },
  { v: "semi_finished", l: "Semi-Finished" },
  { v: "dairy", l: "Dairy" },
  { v: "vegetables", l: "Vegetables" },
  { v: "fruits", l: "Fruits" },
];

const STATUS_FILTERS = [
  { v: "active", l: "Active (hide archived)" },
  { v: "", l: "All statuses" },
  { v: "approved", l: "Approved" },
  { v: "draft", l: "Draft" },
  { v: "blocked", l: "Blocked" },
  { v: "discontinued", l: "Discontinued" },
  { v: "archived", l: "Archived" },
];

export default function Items() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/items", { params: { q, category: cat, status } });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, status]);

  // Safety net for CRUD-triggered reloads (e.g. deleting the last row on the
  // last page) — clamps the current page back into range without yanking the
  // user to page 1 on every reload.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    setPage((p) => Math.min(p, totalPages));
  }, [rows, pageSize]);

  const applyFilters = () => {
    setPage(1);
    load();
  };

  const onSubmit = async (form) => {
    try {
      if (editing) {
        await api.put(`/inventory/items/${editing.id}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", form);
        toast.success("Item created");
      }
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(
      "Delete this item?\n\nIf it has stock, movements, recipes or orders it will be archived instead, so its history is kept."
    )) return;
    try {
      const { data } = await api.delete(`/inventory/items/${id}`);
      if (data?.archived) {
        toast.success(`Archived — still used by ${data.referenced_by.join(", ")}`);
      } else {
        toast.success("Deleted");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div data-testid="inv-items-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
              Inventory · Master
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Item Master</h1>
            <p className="text-sm text-slate-500 mt-1">
              Raw materials, packaging, consumables, finished goods.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
              data-testid="items-import-btn"
            >
              <Upload size={14} weight="bold" /> Import Excel
            </button>
            <button
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              data-testid="items-new-btn"
            >
              <Plus size={14} weight="bold" /> New Item
            </button>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Search by name, code, barcode…"
              className="h-10 w-72 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="items-search-input"
            />
          </div>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            data-testid="items-category-filter"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.v} value={c.v}>{c.l}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            data-testid="items-status-filter"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.v} value={s.v}>{s.l}</option>
            ))}
          </select>
          <button
            onClick={applyFilters}
            className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50"
            data-testid="items-apply-filter"
          >
            Apply
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Code", "Name", "Category", "UoM", "Available", "Reorder", "Expiry", "Status", ""].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-sm text-slate-500">
                    No items yet. Click &quot;New Item&quot; or &quot;Import Excel&quot;.
                  </td>
                </tr>
              )}
              {pageRows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`item-row-${r.id}`}>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{CATEGORIES.find((c) => c.v === r.category)?.l || r.category}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.uom}</td>
                <td className="px-4 py-3 text-sm tabular-nums">{r.available_qty ?? 0}</td>
                <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{r.reorder_level || 0}</td>
                <td className="px-4 py-3" data-testid={`item-expiry-${r.id}`}>
                  <ExpiryCell item={r} />
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => { setEditing(r); setDialogOpen(true); }}
                      className="p-2 text-slate-500 hover:bg-slate-100 rounded"
                      data-testid={`item-edit-${r.id}`}
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(r.id)}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded"
                      data-testid={`item-delete-${r.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={rows.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            testId="items-pagination"
          />
        </div>
      </div>

      <ItemFormDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSubmit={onSubmit}
      />

      <ItemImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />
    </div>
  );
}

// Earliest expiry among the item's QA-released batches. Expired batches are
// excluded from Available, so the date turns red to explain the gap. Batches
// received without an expiry get one estimated from the item's shelf life.
function ExpiryCell({ item }) {
  const { next_expiry: date, next_expiry_estimated: estimated, expired_qty, uom, shelf_life_days } = item;
  const inStock = (item.available_qty || 0) + (expired_qty || 0) > 0;

  if (!date) {
    if (!inStock) {
      return (
        <span className="text-xs text-slate-400 whitespace-nowrap" title="No stock on hand — date is set when stock is received">
          {shelf_life_days > 0 ? `${shelf_life_days}d shelf life` : "No stock"}
        </span>
      );
    }
    return (
      <span className="text-xs text-amber-700 whitespace-nowrap" title="Stock has no expiry date and the item has no shelf life — edit the item to set Shelf Life">
        Not set
      </span>
    );
  }

  const d = new Date(`${date}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  const label = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  let tone = "text-slate-700";
  let hint = `Expires in ${days} day${days === 1 ? "" : "s"}`;
  if (days < 0) {
    tone = "text-rose-700 font-semibold";
    hint = estimated
      ? "Past its shelf life (estimated) — check the batch and record its real expiry"
      : `Expired — ${Number(expired_qty || 0).toLocaleString("en-IN")} ${uom} can't be issued`;
  } else if (days <= 30) {
    tone = "text-amber-700 font-medium";
  }
  if (estimated) hint += " · estimated from shelf life (batch has no expiry recorded)";
  return (
    <span className={`text-xs tabular-nums whitespace-nowrap ${tone}`} title={hint}>
      {label}
      {estimated && <span className="ml-1 text-[10px] text-slate-400 font-normal">est.</span>}
      {days < 0 && (
        <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded">
          Expired
        </span>
      )}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: "bg-emerald-50 text-emerald-700",
    draft: "bg-slate-100 text-slate-600",
    blocked: "bg-rose-50 text-rose-700",
    discontinued: "bg-amber-50 text-amber-700",
    archived: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status || "—"}
    </span>
  );
}
